import React, { useState, useEffect } from "react";
import { secureGet, securePut } from "../../config/api";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import Input from "../../components/Input";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";

import plusIcon from "../../icons/plus.svg";
import deleteIcon from "../../icons/trashR.svg";
import plus from "../../icons/plusP.svg";
import minus from "../../icons/minusP.svg";
import noImage from "../../icons/noImageIcon.svg";
import { useDatePickerDefaultizedProps } from "@mui/x-date-pickers/DatePicker/shared";

const RequestForm = ({
  title,
  baseFields = [],
  listFields = [],
  additionalFields = [],
  initialValues = {},
  onSubmit,
  onSuccess,
  products = [],
}) => {
  const [formValues, setFormValues] = useState({});
  const [productList, setProductList] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const location = useLocation();
  const { request } = location.state || {};
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true); 

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    const checkDataReadiness = () => {
      const baseFieldsReady = baseFields.every(field =>
        !field.options || field.options.length > 0
      );
  
      const isEditMode = !!request || !!initialValues.id;
      
      if (isEditMode) {
        return baseFieldsReady && Object.keys(formValues).length > 0 && productList.length > 0;
      }
      
      return baseFieldsReady;
    };
  
    if (checkDataReadiness()) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [baseFields, formValues, initialValues.id, request, productList]);

  const fetchProductAvailability = async (productId, signal = null) => {
    try {
      const response = await secureGet(`/products/${productId}/availability`, showSnackbar, signal ? { signal } : {});
      const data = response.data;
      return data.availability;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
      return null;
    }
  };


  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;
    
    const initializeForm = async () => {
      let initialFormValues = {
        sourceRepo: initialValues?.sourceRepo || request?.sourceRepo?.id || "",
        destRepo: initialValues?.destRepo || request?.destRepo?.id || "",
        ...initialValues?.fields,
        ...request?.fields,
      };


      setFormValues(initialFormValues);

      if (request) {
        const productAvailabilities = await Promise.all(
          request.details.map(async (detail) => {
            const selectedProduct = products.find((p) => p.value === detail.product.id);

            if (!selectedProduct) {
              console.warn("Product not found:", detail.product.id);
              return null; 
            }

            const availabilityData = await fetchProductAvailability(detail.product.id, signal);
            if (!availabilityData || !selectedProduct) {
              return null;
            }

            const stockSourceAvailability = availabilityData.find(
              (item) => item.repoId === request.sourceRepo?.id
            )?.quantity || 0;

            const stockDestAvailability = availabilityData.find(
              (item) => item.repoId === request.destRepo?.id
            )?.quantity || 0;

            return {
              id: detail.product.id,
              reference: selectedProduct.reference,
              name: selectedProduct.name,
              image: selectedProduct.fullEntity?.image,
              stockSourceAvailability,
              stockDestAvailability,
              quantity: detail.quantity,
              newStockSource: stockSourceAvailability - detail.quantity,
              newStockDest: stockDestAvailability + detail.quantity,
              sourceRepoLabel: request.sourceRepo?.name || "N/A",
              destRepoLabel: request.destRepo?.name || "N/A",
            };
          })
        );

        setProductList(productAvailabilities.filter((product) => product !== null));
      }
    };

    initializeForm();
    return () => {
      abortController.abort();
    };
  }, [initialValues, request, products]);


  const getLabelFromOptions = (options, value) => {
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : "Unknown";
  };

  const handleInputChange = async (name, value) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));

    if (name === "sourceRepo" || name === "destRepo") {
      const isSourceRepo = name === "sourceRepo";

      const repoField = baseFields.find((field) => field.name === name);
      const updatedRepoLabel = getLabelFromOptions(repoField?.options || [], value);

      const updatedProductList = await Promise.all(
        productList.map(async (product) => {
          const availabilityData = await fetchProductAvailability(product.id);
          if (!availabilityData) {
            return product;
          }

          const updatedStockAvailability = availabilityData.find(
            (item) => Number(item.repoId) === Number(value)
          )?.quantity || 0;

          return {
            ...product,
            ...(isSourceRepo
              ? {
                stockSourceAvailability: updatedStockAvailability,
                sourceRepoLabel: updatedRepoLabel,
                newStockSource: updatedStockAvailability - product.quantity,
              }
              : {
                stockDestAvailability: updatedStockAvailability,
                destRepoLabel: updatedRepoLabel,
                newStockDest: updatedStockAvailability + product.quantity,
              }),
          };
        })
      );

      setProductList(updatedProductList);
    }
  };

  const addBaseFieldsToList = async () => {

      if (String(formValues.sourceRepo) === String(formValues.destRepo)) {
    showSnackbar(t('errors.sameSourceDest'), 'error');
    return;
  }

    for (const field of [...baseFields, ...listFields]) {
      if (!formValues[field.name]) {
        showSnackbar(t('placeholder.empty_fields'), 'error');
        return;
      }
    }

    const selectedProduct = products.find(
      (product) => product.value === formValues.product
    );

    if (!selectedProduct) {
      showSnackbar(t('placeholder.product_not_found'), 'error');
      return;
    }

    const availabilityData = await fetchProductAvailability(selectedProduct.value);
    if (!availabilityData) {
      showSnackbar(t('placeholder.availability_error'), 'error');
      return;
    }

    const stockSourceAvailability = availabilityData.find(
      (item) => item.repoId === formValues.sourceRepo
    )?.quantity || 0;

    const stockDestAvailability = availabilityData.find(
      (item) => item.repoId === formValues.destRepo
    )?.quantity || 0;

    if (stockSourceAvailability <= 0) {
      showSnackbar(t('placeholder.repoError'), 'error');
      return;
    }

    const sourceRepoField = baseFields.find((field) => field.name === "sourceRepo");
    const destRepoField = baseFields.find((field) => field.name === "destRepo");
    const sourceRepoLabel = getLabelFromOptions(sourceRepoField?.options || [], formValues.sourceRepo);
    const destRepoLabel = getLabelFromOptions(destRepoField?.options || [], formValues.destRepo);

    const newQuantity = parseInt(formValues.quantity, 10);

    setProductList((prevList) => {
      // Check if product with same ID, sourceRepo, and destRepo already exists
      const existingIndex = prevList.findIndex(
        (item) => 
          item.id === selectedProduct.value &&
          item.sourceRepoLabel === sourceRepoLabel &&
          item.destRepoLabel === destRepoLabel
      );

      if (existingIndex !== -1) {
        // Update existing product quantity
        const updatedList = [...prevList];
        const existingProduct = updatedList[existingIndex];
        const updatedQuantity = existingProduct.quantity + newQuantity;
        
        updatedList[existingIndex] = {
          ...existingProduct,
          quantity: updatedQuantity,
          newStockSource: existingProduct.stockSourceAvailability - updatedQuantity,
          newStockDest: existingProduct.stockDestAvailability + updatedQuantity,
        };
        return updatedList;
      } else {
        // Add new product
        const product = {
          id: selectedProduct.value,
          reference: selectedProduct.reference,
          name: selectedProduct.name,
          image: selectedProduct.fullEntity.image,
          stockSourceAvailability,
          stockDestAvailability,
          sourceRepoLabel,
          destRepoLabel,
          quantity: newQuantity,
          newStockSource: stockSourceAvailability - newQuantity,
          newStockDest: stockDestAvailability + newQuantity,
        };
        return [...prevList, product];
      }
    });

    setFormValues((prevValues) => ({
      ...prevValues,
      product: "",
      quantity: "",
    }));
  };

  const handleQuantityChange = (index, newQuantity) => {
    setProductList((prevList) =>
      prevList.map((product, i) =>
        i === index
          ? {
            ...product,
            quantity: Math.min(
              newQuantity,
              product.stockSourceAvailability
            ),
            newStockSource:
              product.stockSourceAvailability - Math.min(newQuantity, product.stockSourceAvailability),
            newStockDest:
              product.stockDestAvailability + Math.min(newQuantity, product.stockSourceAvailability),
          }
          : product
      )
    );
  };

  const removeProductFromList = (index) => {
    setProductList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // In modify mode, listFields are not required if productList already exists
    const fieldsToValidate = productList.length > 0 
        ? [...baseFields, ...additionalFields] 
        : [...baseFields, ...listFields, ...additionalFields];
        
    const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
    if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
    }  

    const payload = {
      draftId: request?.id ? request.id : null,
      // ...(initialValues?.id && { draftId: initialValues.id }),
      destRepoId: Number(formValues.destRepo),
      sourceRepoId: Number(formValues.sourceRepo),
      products: productList.map((product) => ({
        productId: product.id,
        quantity: product.quantity,
      })),
      fields: formValues.additionalFields || {},
    };


    try {
      const response = await securePut("/repos/transfer/draft/add", payload, showSnackbar);
      if (response && response.status === 200) {
        onSuccess();
        onSubmit(response.data);

      } else {
        console.error("Transfer Failed:", response);
      }
    } catch (error) {
      console.error("Error Submitting Transfer:", error);
    }
  };

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "50px";
    e.target.style.height = "50px";
  };



  return (
    <div className="w-full">
      <Helmet>
        <title>GrayFox - {t("titles.requests")}</title>
      </Helmet>
      <Breadcrumbs />
      <h2 className="text-[40px] text-[#000000] font-semibold mb-4">{title}</h2>
      {loading ? (
        <div className="flex justify-center items-center h-[300px]">
          <LoadingLogo />
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...baseFields, ...listFields].map((field, index) => (
                <Input
                  key={index}
                  type={field.type || "text"}
                  label={field.label}
                  name={field.name}
                  value={formValues[field.name] || ""}
                  required={field.required || false}
                  onChange={(value) => handleInputChange(field.name, value)}
                  options={
                    field.name === "product"
                      ? products.map((product) => ({
                        value: product.value,
                        name: product.name,
                        reference: product.reference,
                        fullEntity: product.fullEntity,
                      }))
                      : field.options || []
                  }
                />
              ))}
            </div>
            <button
              type="button"
              onClick={addBaseFieldsToList}
              className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
            >
              <img src={plusIcon} alt="Add" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Additional Fields */}
        {additionalFields.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.additional`)}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {additionalFields.map((field, index) => (
                <Input
                  key={index}
                  label={field.label}
                  name={field.name}
                  type={field.type || "text"}
                  value={formValues[field.name] || ""}
                  required={field.required || false}
                  onChange={(value) => handleInputChange(field.name, value)}
                  options={field.options || []}
                />
              ))}
            </div>
          </div>
        )}

        <p className="text-[#EF476F] text-[12px] font-medium mb-2">{t(`placeholder.transferWarning`)}</p>


        {/* Product List */}
        {productList.length > 0 && (
          <div className="mt-4">
            {/* Header Row */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] items-center text-[#919191] text-sm font-semibold mb-2">
              <span className="text-left">{t(`rows.product`)}</span>
              <span className="text-center">{t(`rows.currentStock`)}</span>
              <span className="text-center"></span>
              <span className="text-center flex justify-left">{t(`rows.quantity`)}</span>
              <span className="text-center">{t(`rows.newStock`)}</span>
              <span className="text-center"></span>
              <span className="text-right"></span>
            </div>

            {/* Product Rows */}
            {productList.map((product, index) => (
              <div
                key={index}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] items-center py-4 px-4 rounded-lg bg-white shadow-[0px_4px_16px_#626B7D12] mb-4 gap-4"
              >
                {/* Product Details */}
                <div className="flex items-center gap-4">
                  <div className="w-[60px] h-[75px] flex items-center justify-center flex-shrink-0 rounded-md overflow-hidden shadow-[0px_4px_16px_#626B7D12]">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain"
                      onError={handleImageError}
                    />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-[#043F88] text-sm font-semibold">
                      {product.reference || ""}
                    </p>
                    <p className="text-[#6B78F8] text-base font-semibold leading-tight">
                      {product.name || ""}
                    </p>
                    <p className="text-[#ACAFC6] text-sm font-medium">
                      {product.description || ""}
                    </p>
                  </div>
                </div>

                {/* Stock Source */}
                <div className="text-center">
                  <p className="text-[#6B78F8] text-xl font-semibold">
                    {product.stockSourceAvailability}
                  </p>
                  <p className="text-[#ACAFC6] text-sm font-medium">
                    {product.sourceRepoLabel}
                  </p>
                </div>

                {/* Stock Destination */}
                <div className="text-center">
                  <p className="text-[#6B78F8] text-xl font-semibold">
                    {product.stockDestAvailability}
                  </p>
                  <p className="text-[#ACAFC6] text-sm font-medium">
                    {product.destRepoLabel}
                  </p>
                </div>

                {/* Quantity */}
                <div className="flex justify-center items-center gap-2">
                  <div className="flex items-stretch rounded-lg border border-[#6C78F8] bg-[#6B78F858] divide-x divide-[#6C78F8]">
                    <button
                      type="button"
                      onClick={() =>
                        handleQuantityChange(index, Math.max(product.quantity - 1, 1))
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                    >
                      <img src={minus} alt="Minus" className="w-4 h-4" />
                    </button>
                    <span className="w-8 h-8 flex items-center justify-center font-semibold text-[#38485C] text-base">
                      {product.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleQuantityChange(index, product.quantity + 1)
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                    >
                      <img src={plus} alt="Plus" className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Nouveau Stock Source */}
                <div className="text-center">
                  <p className="text-[#16C79A] text-xl font-semibold">
                    {product.newStockSource}
                  </p>
                  <p className="text-[#ACAFC6] text-sm font-medium">
                    {product.sourceRepoLabel}
                  </p>
                </div>

                {/* Nouveau Stock Destination */}
                <div className="text-center">
                  <p className="text-[#16C79A] text-xl font-semibold">
                    {product.newStockDest}
                  </p>
                  <p className="text-[#ACAFC6] text-sm font-medium">
                    {product.destRepoLabel}
                  </p>
                </div>

                {/* Delete Icon */}
                <div className="flex justify-end">
                  <button
                    className="w-8 h-8 flex items-center justify-center rounded-[12px] shadow-[0px_4px_16px_#626B7D12] cursor-pointer hover:bg-red-50"
                    onClick={() => removeProductFromList(index)}
                  >
                    <img src={deleteIcon} alt="Plus" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}


        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-semibold text-[18px] px-8 py-2 rounded-[12px]"
          >
            {t(`actions.validate`)}
          </button>
        </div>
      </form>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default RequestForm;
